import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Grid,
    TextField,
    CircularProgress,
    IconButton,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ModuleListModal from './ModuleListModal';
import { addTopic, getAllModules, updateTopic } from '../../../services/adminServices/ModuleServices';
import { toast } from 'react-toastify';

const INITIAL_FORM_STATE = { module: '', topic: '', notes: '', level1: '', level2: '', level3: '' };

const MakerModuleModal = ({ open, onClose, modules, fetchModules, rowData }) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModuleModalOpen, setIsAddModuleModalOpen] = useState(false);
    const [moduleList, setModuleList] = useState([]);

    useEffect(() => {
        if (!open) resetFormState();
    }, [open]);


    const resetFormState = () => {
        setFormValues(INITIAL_FORM_STATE);
        setFormErrors({});
        setIsLoading(false);
    };

    const handleInputChange = ({ target: { name, value, files } }) => {
        setFormValues((prev) => ({
            ...prev,
            [name]: files ? files[0] : value,
        }));
    };
    useEffect(() => {
        if (open) {
            setFormValues(INITIAL_FORM_STATE);
            setFormErrors({});
        }
    }, [open]);
    useEffect(() => {
        if (rowData != null) {
            setFormValues({
                module:rowData.moduleDetails?.moduleID || '',
                topic: rowData.topicDetails?.topic_Name || '',
                notes: rowData.topicDetails?.topic_pdfPath || '',
                level1: rowData.topicDetails?.level1_practicePdf || '',
                level2: rowData.topicDetails?.level2_practicePdf || '',
                level3: rowData.topicDetails?.level3_practicePdf || ''
            });
        }
        else {
            setFormValues(INITIAL_FORM_STATE);
        }
        setFormErrors({});

    }, [rowData]);

    async function getModules() {
        try {
            const modules = await getAllModules();
            setModuleList(modules?.data?.data);
        } catch (error) {
            toast.error( error?.response?.data.message||error?.message ||error?.error);
            setModuleList([]);
        }
    };
    useEffect(() => {
        getModules();
    }, [])

    const validateFields = () => {
        const errors = {};
        Object.keys(formValues).forEach((field) => {
            if (!formValues[field]) errors[field] = `${field.replace(/^\w/, (c) => c.toUpperCase())} is required*`;
        });
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        setIsLoading(true);

        const obj = {
            topic_Name: formValues.topic,
            topic_pdfPath: formValues.notes,
            level1_practicePdf: formValues.level1,
            level2_practicePdf: formValues.level2,
            level3_practicePdf: formValues.level3
        }

        try {
            if (rowData != null) {
                const response = await updateTopic(rowData?.topicDetails.topicID,formValues?.module,  obj)
                toast.success(response.data.message);

            } else {
                const response = await addTopic(formValues.module, obj)
                toast.success(response.data.message);
            }
            resetFormState();
            fetchModules();
            onClose();
        } catch (error) {
            toast.error( error?.response?.data.message||error?.message ||error?.error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleModuleSelectOpen = () => {
        setIsAddModuleModalOpen(true);
    };

    const handleModuleSelectClose = () => {
        setIsAddModuleModalOpen(false);
    };

    const renderFileOrPathField = (label, name) => (
        <Grid item xs={12} container alignItems="center" spacing={2}>
            <Grid item xs={6}>
                <Typography fontSize="16px" variant="body1">{label}</Typography>
            </Grid>
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    size="small"
                    name={name}
                    value={formValues[name]}
                    onChange={handleInputChange}
                    error={!!formErrors[name]}
                    helperText={formErrors[name]}
                    placeholder={` ${label}path`}
                />
                <input
                    type="file"
                    name={name}
                    id={`file-input-${name}`}
                    hidden
                    onChange={handleInputChange}
                />
            </Grid>
        </Grid>
    );

    const uploadFields = [
        { label: 'Notes', name: 'notes' },
        { label: 'Level 1 Practice', name: 'level1' },
        { label: 'Level 2 Practice', name: 'level2' },
        { label: 'Level 3 Practice', name: 'level3' },
    ];

    return (
        <>
            <Modal open={open} onClose={onClose} BackdropProps={{ onClick: onClose }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '50%',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Create Module
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} container alignItems="center" spacing={2}>
                                <Grid item xs={11}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Select Module*"
                                        name="module"
                                        value={formValues.module}
                                        onChange={handleInputChange}
                                        error={!!formErrors.module}
                                        helperText={formErrors.module}
                                        SelectProps={{
                                            native: true,
                                        }}
                                    >
                                        <option value="" disabled></option>
                                        {moduleList?.map((module) => (
                                            <option key={module.moduleID} value={module.moduleID}>
                                                {module.module_Name}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={1}>
                                    <Tooltip title="Add Module" arrow>
                                        <IconButton onClick={handleModuleSelectOpen}>
                                            <AddIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Topic *"
                                    name="topic"
                                    size="small"
                                    value={formValues.topic}
                                    onChange={handleInputChange}
                                    error={!!formErrors.topic}
                                    helperText={formErrors.topic}
                                />
                            </Grid>
                            {uploadFields.map(({ label, name }) => renderFileOrPathField(label, name))}
                            <Grid item xs={12} container justifyContent="center" spacing={2}>
                                <Grid item>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? <CircularProgress size={24} /> : 'Submit'}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="outlined" color="secondary" onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Modal>
            <ModuleListModal
                handleModuleSelectClose={handleModuleSelectClose}
                isAddModuleModalOpen={isAddModuleModalOpen}
                fetchModules={getModules}
                modules={moduleList}
            />
        </>
    );
};

export default MakerModuleModal;
